import type { ApiErrorBody, ErrorCategory } from '@cercle/shared';

/** Le port. Les adaptateurs de fonctionnalité en dépendent, jamais de `fetch` directement. */
export interface HttpClient {
  get<T>(path: string, init?: RequestInit): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  /**
   * Envoi de formulaire multipart, pour les fichiers.
   *
   * Distinct de `post` parce qu'il ne faut surtout **pas** poser `Content-Type` :
   * le navigateur doit le composer lui-même avec la frontière du multipart. Le fixer à la
   * main produit un corps que le serveur ne sait pas découper, et l'envoi arrive vide.
   */
  postForm<T>(path: string, form: FormData): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  delete<T>(path: string): Promise<T>;
  /** Envoi brut, sans lever d'exception : utilisé par la file offline qui veut le statut. */
  raw(path: string, init: RequestInit): Promise<{ status: number; body: unknown }>;
}

/** Erreur portant le corps normalisé de l'API, pour que l'UI affiche un message français. */
export class HttpError extends Error {
  constructor(
    readonly status: number,
    readonly category: ErrorCategory,
    message: string,
    readonly code?: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

export function isHttpError(error: unknown): error is HttpError {
  return error instanceof HttpError;
}

/** Message affichable pour n'importe quelle erreur remontée d'un appel. */
export function errorMessage(error: unknown): string {
  if (isHttpError(error)) return error.message;
  if (error instanceof Error && error.message === 'Failed to fetch') {
    return 'Pas de réseau. Votre saisie sera envoyée à la reconnexion.';
  }
  return 'Une erreur est survenue.';
}

export function toApiError(status: number, body: unknown): HttpError {
  const parsed = body as Partial<ApiErrorBody> | null;
  return new HttpError(
    status,
    parsed?.category ?? 'unavailable',
    parsed?.message ?? 'Une erreur est survenue.',
    parsed?.code,
    parsed?.details,
  );
}
